/**
 * Deutsche Meldungen für die Prüfung der Browser. Die Texte „Value must be 31/10/2026 or earlier“ oder „Please fill out this field“ richten sich nach der Sprache des Browsers
 * und nicht nach der Seite, `lang="de"` ändert daran nichts. Hier wird jede Meldung beim Auslösen durch einen deutschen Text ersetzt und beim nächsten Eingeben wieder freigegeben,
 * sonst bliebe das Feld als ungültig markiert. Die Prüfung selbst (Pflichtfeld, Grenzen, Format) übernimmt weiter der Browser.
 */
type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const dateDe = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso);
const monthDe = (iso: string) => (/^\d{4}-\d{2}$/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso);
const shown = (el: HTMLInputElement, value: string) => (el.type === 'date' ? dateDe(value) : el.type === 'month' ? monthDe(value) : value);

export function germanMessage(el: Field): string {
  const v = el.validity;
  const input = el as HTMLInputElement;
  if (v.valueMissing) {
    if (el instanceof HTMLSelectElement) return 'Bitte wählen Sie einen Eintrag aus.';
    if (input.type === 'checkbox') return 'Bitte setzen Sie dieses Häkchen, wenn Sie fortfahren möchten.';
    if (input.type === 'radio') return 'Bitte wählen Sie eine der Möglichkeiten aus.';
    if (input.type === 'file') return 'Bitte wählen Sie eine Datei aus.';
    return 'Bitte füllen Sie dieses Feld aus.';
  }
  if (v.typeMismatch) return input.type === 'email' ? 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' : input.type === 'url' ? 'Bitte geben Sie eine gültige Internetadresse ein.' : 'Bitte geben Sie einen gültigen Wert ein.';
  if (v.rangeOverflow) return input.type === 'date' || input.type === 'month' || input.type === 'time' ? `Bitte wählen Sie einen Wert bis ${shown(input, input.max)}.` : `Der Wert darf höchstens ${input.max} sein.`;
  if (v.rangeUnderflow) return input.type === 'date' || input.type === 'month' || input.type === 'time' ? `Bitte wählen Sie einen Wert ab ${shown(input, input.min)}.` : `Der Wert muss mindestens ${input.min} sein.`;
  if (v.tooShort) return `Bitte verwenden Sie mindestens ${input.minLength} Zeichen.`;
  if (v.tooLong) return `Bitte verwenden Sie höchstens ${input.maxLength} Zeichen.`;
  if (v.patternMismatch) return input.title || 'Bitte halten Sie sich an das geforderte Format.';
  if (v.stepMismatch) return 'Bitte geben Sie einen gültigen Wert ein.';
  if (v.badInput) return 'Bitte geben Sie einen gültigen Wert ein.';
  return 'Bitte prüfen Sie diese Eingabe.';
}

/** Einmal beim Start aufrufen */
export function installGermanValidation(): void {
  document.addEventListener(
    'invalid',
    (e) => {
      const el = e.target as Field;
      if (!('setCustomValidity' in el)) return;
      el.setCustomValidity('');
      el.setCustomValidity(germanMessage(el));
    },
    true,
  );
  const clear = (e: Event) => {
    const el = e.target as Field;
    if ('setCustomValidity' in el) el.setCustomValidity('');
  };
  document.addEventListener('input', clear, true);
  document.addEventListener('change', clear, true);
}
